import React, { useState } from 'react';
import { ChevronLeft, ChevronDown, ChevronUp, HelpCircle, MessageCircle } from 'lucide-react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAppContext } from '../context/AppContext';
import { v2, statusMap, getStatusTone } from '../constants';
import FIXIChat from '../components/FIXIChat';

const SUPPORT_FAQS = [
  {
    id: 'price',
    icon: '💰',
    q_es: '¿Cuánto cuesta el servicio?',
    q_zh: '服务多少钱？',
    a_es: 'Los precios varían según el servicio:\n\n• Limpieza de A/C: $45 por unidad\n• Electricista: desde $35 (solo mano de obra)\n• Plomero: desde $40 (solo mano de obra)\n\nEl monto exacto lo ves antes de confirmar el pago. Sin sorpresas.',
    a_zh: '各服务价格：\n\n• 空调清洗：每台 $45\n• 电工：起价 $35（纯人工费）\n• 水工：起价 $40（纯人工费）\n\n付款前可看到确切金额，无隐藏费用。'
  },
  {
    id: 'warranty',
    icon: '🛡️',
    q_es: '¿Qué garantía tienen los servicios?',
    q_zh: '服务有什么保障？',
    a_es: 'Todos los servicios tienen Garantía FixBox de 30 días.\n\nSi el problema regresa dentro de ese plazo, el técnico vuelve sin costo adicional. Si no quedas satisfecho, puedes abrir una disputa desde la app y recuperar tu dinero.',
    a_zh: '所有服务均享有30天FixBox质保。\n\n如果问题在此期限内复发，师傅免费再次上门。如不满意，可在App内发起申诉取回款项。'
  },
  {
    id: 'escrow',
    icon: '🔒',
    q_es: '¿Cómo está protegido mi dinero?',
    q_zh: '我的钱安全吗？',
    a_es: 'Usamos un sistema de escrow (pago en custodia):\n\n1. Pagas al hacer el pedido\n2. El dinero queda retenido en FixBox\n3. El técnico realiza el trabajo\n4. Tú apruebas → solo entonces liberamos el pago\n\nNunca pierdes tu dinero sin haber recibido el servicio.',
    a_zh: '使用资金托管（Escrow）机制：\n\n1. 下单时付款\n2. 资金由FixBox托管\n3. 师傅完成工作\n4. 您确认验收 → 才释放款项\n\n未完工前资金绝对安全。'
  },
  {
    id: 'cancel',
    icon: '❌',
    q_es: '¿Puedo cancelar mi pedido?',
    q_zh: '可以取消订单吗？',
    a_es: 'Sí. Si el técnico aún no ha empezado el trabajo, puedes cancelar desde "Mis Pedidos" y el reembolso va a tu billetera FixBox de inmediato.\n\nSi el técnico ya llegó o comenzó, contáctanos por WhatsApp.',
    a_zh: '可以。如果师傅还未开始工作，可在"我的订单"页面取消，全额立即退回钱包余额。\n\n如师傅已上门或开工，请联系WhatsApp客服。'
  },
  {
    id: 'invoice',
    icon: '📄',
    q_es: '¿Emiten factura fiscal?',
    q_zh: '可以开发票吗？',
    a_es: 'FixBox emite recibo de servicio para todos los pedidos.\n\nPara factura fiscal con RUC (empresa), activa esa opción al hacer el pedido. Se asignará automáticamente un técnico con capacidad de facturación.',
    a_zh: '所有订单均提供服务收据。\n\n如需开具正式发票（含RUC，用于企业），下单时勾选该选项，系统会自动匹配具有开票资质的师傅。'
  },
  {
    id: 'zones',
    icon: '🗺️',
    q_es: '¿En qué zonas operan?',
    q_zh: '服务哪些区域？',
    a_es: 'Zonas activas (Fase 1):\n• Bella Vista · San Francisco · Marbella\n• El Cangrejo · Punta Pacífica · Paitilla\n• Vía España · Betania · Costa del Este\n\nMás zonas próximamente. Si tienes dudas, escríbenos por WhatsApp.',
    a_zh: '当前服务区域（第一期）：\n• Bella Vista · San Francisco · Marbella\n• El Cangrejo · Punta Pacífica · Paitilla\n• Vía España · Betania · Costa del Este\n\n更多区域陆续开通。有疑问可WhatsApp联系。'
  },
  {
    id: 'materials',
    icon: '🔩',
    q_es: '¿El técnico trae materiales?',
    q_zh: '师傅会带材料吗？',
    a_es: 'Los servicios de Electricista y Plomero cubren solo la mano de obra (visita + diagnóstico + trabajo).\n\nLos materiales de repuesto NO están incluidos. Si necesitas piezas, el técnico te dice qué comprar y tú decides dónde comprarlo.',
    a_zh: '电工和水工服务仅含人工费（上门+诊断+施工）。\n\n备件和材料不含在内。如需零件，师傅会告知您需要购买什么，由您自行决定采购渠道。'
  }
];

export default function SuggestionView() {
  const {
    lang, user, userData, orders, setView,
    showToast, openWhatsApp, PLATFORM_WA
  } = useAppContext();

  const [openFaq, setOpenFaq] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestText, setSuggestText] = useState('');
  const [sending, setSending] = useState(false);
  const [fixiOpen, setFixiOpen] = useState(false);

  // Find client's active order for contextual reference
  const activeOrder = orders?.find(o =>
    o.client_id === user?.uid &&
    ['OPEN', 'ACCEPTED', 'ASSIGNED', 'WORKING', 'PENDING_APPROVAL'].includes(o.status)
  );
  const statusTone = activeOrder ? getStatusTone(activeOrder.status) : null;

  const waMsg = activeOrder
    ? (lang === 'es'
      ? `Hola FixBox, necesito ayuda con mi pedido ${activeOrder.order_id}.`
      : `您好FixBox，我需要关于订单 ${activeOrder.order_id} 的帮助。`)
    : (lang === 'es' ? 'Hola FixBox, necesito ayuda.' : '您好FixBox，我需要帮助。');

  const handleSendSuggestion = async () => {
    if (!suggestText.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'Suggestions'), {
        client_id: user.uid,
        client_name: userData?.name || user.email,
        phone: userData?.phone || '',
        request: suggestText.trim(),
        createdAt: serverTimestamp()
      });
      showToast(
        lang === 'es' ? '✅ Solicitud enviada' : '✅ 已发送',
        lang === 'es' ? 'Te contactaremos pronto por WhatsApp.' : '我们会尽快通过WhatsApp联系您。'
      );
      setSuggestText('');
      setShowSuggest(false);
    } catch (e) {
      showToast('Error', e.message);
    }
    setSending(false);
  };

  return (
    <div style={{ padding: '16px 16px 32px', maxWidth: '520px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <button
          onClick={() => setView('home')}
          style={{ width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #D8DEE8', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <ChevronLeft size={22} />
        </button>
        <div>
          <p style={{ margin: '0 0 2px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>
            {lang === 'es' ? 'Ayuda & Asistencia' : '帮助与支持'}
          </p>
          <h2 style={{ fontWeight: '900', margin: 0, color: v2.colors.ink, fontSize: '20px' }}>
            {lang === 'es' ? 'Centro de Soporte' : '用户支持中心'}
          </h2>
        </div>
      </div>

      {/* Active Order Reference */}
      {activeOrder && (
        <div
          onClick={() => setView('history')}
          style={{ background: statusTone?.bg || '#F8FAFC', border: `1px solid ${statusTone?.border || '#E3E8EF'}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '16px', cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: v2.colors.muted, fontWeight: '700' }}>
                {lang === 'es' ? 'Tu pedido activo' : '当前活跃订单'}
              </p>
              <b style={{ fontSize: '15px', color: v2.colors.ink }}>{activeOrder.order_id}</b>
            </div>
            <span style={{ background: statusTone?.bg, color: statusTone?.color, border: `1px solid ${statusTone?.border}`, borderRadius: '8px', padding: '5px 8px', fontSize: '10px', fontWeight: '900', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {statusMap[lang]?.[activeOrder.status] || activeOrder.status}
            </span>
          </div>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: statusTone?.color || v2.colors.muted, fontWeight: '700' }}>
            {lang === 'es' ? 'Toca para ver detalles →' : '点击查看详情 →'}
          </p>
        </div>
      )}

      {/* Quick Contact Buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
        <button
          onClick={() => openWhatsApp(PLATFORM_WA, waMsg)}
          style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: '14px', padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(37,211,102,0.35)' }}
        >
          <span style={{ fontSize: '24px' }}>💬</span>
          <b style={{ fontSize: '14px' }}>WhatsApp</b>
          <span style={{ fontSize: '11px', opacity: 0.9 }}>{lang === 'es' ? 'Soporte humano' : '人工客服'}</span>
        </button>

        <button
          onClick={() => setFixiOpen(true)}
          style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', color: '#fff', border: 'none', borderRadius: '14px', padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' }}
        >
          <span style={{ fontSize: '24px' }}>🤖</span>
          <b style={{ fontSize: '14px' }}>FIXI IA</b>
          <span style={{ fontSize: '11px', opacity: 0.9 }}>{lang === 'es' ? 'Chat en la app' : 'App内AI聊天'}</span>
        </button>
      </div>

      {/* Availability banner */}
      <div style={{ background: '#F0FFF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '10px 14px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '16px' }}>🟢</span>
        <p style={{ margin: 0, fontSize: '12px', color: '#15803D', fontWeight: '700', lineHeight: '1.4' }}>
          {lang === 'es'
            ? 'Soporte disponible · Lun–Sab 8am–8pm · WhatsApp siempre activo'
            : '客服在线：周一至周六 8am–8pm · WhatsApp 24小时可发送'}
        </p>
      </div>

      {/* FAQ Section */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 14px 0', fontSize: '16px', color: v2.colors.ink, fontWeight: '900', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HelpCircle size={18} color="#FF9500" />
          {lang === 'es' ? 'Preguntas frecuentes' : '常见问题解答'}
        </h3>

        {SUPPORT_FAQS.map((faq, idx) => {
          const isOpen = openFaq === faq.id;
          return (
            <div key={faq.id} style={{ borderTop: idx === 0 ? 'none' : '1px solid #EEF2F6', paddingTop: idx === 0 ? 0 : '12px', marginBottom: '12px' }}>
              <button
                onClick={() => setOpenFaq(isOpen ? null : faq.id)}
                style={{ width: '100%', background: 'none', border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: 0, gap: '10px', textAlign: 'left' }}
              >
                <span style={{ fontSize: '14px', color: v2.colors.ink, fontWeight: '800', lineHeight: '1.35' }}>
                  {faq.icon} {lang === 'es' ? faq.q_es : faq.q_zh}
                </span>
                {isOpen
                  ? <ChevronUp size={17} color={v2.colors.muted} style={{ flexShrink: 0 }} />
                  : <ChevronDown size={17} color={v2.colors.muted} style={{ flexShrink: 0 }} />
                }
              </button>
              {isOpen && (
                <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: '#475569', lineHeight: '1.65', whiteSpace: 'pre-line', background: '#F8FAFC', border: '1px solid #E3E8EF', borderRadius: '8px', padding: '12px' }}>
                  {lang === 'es' ? faq.a_es : faq.a_zh}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Still need help? */}
      <div style={{ background: '#FFF7E6', border: '1px solid #FDE68A', borderRadius: '12px', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '14px' }}>
        <span style={{ fontSize: '28px', flexShrink: 0 }}>💡</span>
        <div style={{ minWidth: 0 }}>
          <b style={{ fontSize: '14px', color: '#92400E', display: 'block', marginBottom: '4px' }}>
            {lang === 'es' ? '¿No encontraste lo que buscabas?' : '没找到您需要的答案？'}
          </b>
          <p style={{ margin: 0, fontSize: '12px', color: '#B45309', lineHeight: '1.45' }}>
            {lang === 'es'
              ? 'Escríbenos por WhatsApp y te ayudamos personalmente.'
              : '通过WhatsApp联系我们，我们会一对一为您解答。'}
          </p>
          <button
            onClick={() => openWhatsApp(PLATFORM_WA, waMsg)}
            style={{ marginTop: '10px', padding: '8px 16px', background: '#25D366', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '900', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <MessageCircle size={14} />
            {lang === 'es' ? 'Escribir a soporte' : '联系客服'}
          </button>
        </div>
      </div>

      {/* FIXI AI Chat (floating overlay, triggered by button) */}
      <FIXIChat lang={lang} forceOpen={fixiOpen} onClose={() => setFixiOpen(false)} />

      {/* Special Service Request (collapsible) */}
      <div style={{ ...v2.card, padding: '16px' }}>
        <button
          onClick={() => setShowSuggest(!showSuggest)}
          style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
        >
          <div>
            <b style={{ fontSize: '15px', color: v2.colors.ink, display: 'block' }}>
              {lang === 'es' ? '¿No encuentras tu servicio?' : '找不到您需要的服务？'}
            </b>
            <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: v2.colors.muted }}>
              {lang === 'es' ? 'Solicitar un servicio especial' : '提交定制服务需求'}
            </p>
          </div>
          {showSuggest
            ? <ChevronUp size={18} color={v2.colors.muted} style={{ flexShrink: 0 }} />
            : <ChevronDown size={18} color={v2.colors.muted} style={{ flexShrink: 0 }} />
          }
        </button>

        {showSuggest && (
          <div style={{ marginTop: '14px' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: v2.colors.muted }}>
              {lang === 'es'
                ? 'Cuéntanos qué servicio necesitas y te contactaremos.'
                : '告诉我们您需要什么服务，我们会联系您跟进。'}
            </p>
            <textarea
              placeholder={lang === 'es'
                ? 'Ej: Pintar la sala, instalar cortinas, limpiar sofás...'
                : '例如：刷墙、装窗帘、清洁沙发...'}
              value={suggestText}
              onChange={e => setSuggestText(e.target.value)}
              style={{ width: '100%', height: '100px', padding: '12px', borderRadius: '10px', border: '1px solid #D8DEE8', fontSize: '14px', boxSizing: 'border-box', resize: 'none', fontFamily: 'inherit', outline: 'none' }}
            />
            <button
              onClick={handleSendSuggestion}
              disabled={!suggestText.trim() || sending}
              style={{ width: '100%', padding: '14px', backgroundColor: (!suggestText.trim() || sending) ? '#E2E8F0' : '#FF9500', color: (!suggestText.trim() || sending) ? '#94A3B8' : '#fff', borderRadius: '10px', border: 'none', marginTop: '10px', fontWeight: '900', fontSize: '15px', cursor: 'pointer', transition: 'background 0.2s' }}
            >
              {sending
                ? (lang === 'es' ? 'Enviando...' : '发送中...')
                : (lang === 'es' ? 'Enviar solicitud' : '发送需求')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
